import { DetectionHit, extractDetectionHits } from "./formatMissionLogDisplay";

type Attachment = { id: string };
type ClipJob = { status: string; attachmentId: string | null };

export type MergeableMissionLogEvent<
    TAttachment extends Attachment = Attachment,
    TClipJob extends ClipJob = ClipJob,
> = {
    id: string;
    type: string;
    detail: string;
    payload: string;
    data: unknown;
    attachments: TAttachment[];
    clipJobs: TClipJob[];
};

export type MergedMissionLogEvent<TEvent> = TEvent & {
    detectionHits?: DetectionHit[];
    mergedEventCount: number;
};

const DETECTION_RESULT_TOPICS = new Set([
    "rtsp_inference/result",
    "thermal_detection/result",
]);

const isDetectionResultTopic = (type: string): boolean =>
    DETECTION_RESULT_TOPICS.has(type.replace(/^\//, ""));

const areRepeats = <TEvent extends MergeableMissionLogEvent>(
    previous: TEvent,
    current: TEvent,
): boolean => {
    if (previous.type !== current.type) return false;
    if (isDetectionResultTopic(previous.type)) return true;

    return previous.detail === current.detail && previous.payload === current.payload;
};

const uniqueAttachments = <TAttachment extends Attachment>(
    attachments: TAttachment[],
): TAttachment[] => {
    const seen = new Set<string>();
    return attachments.filter((attachment) => {
        if (seen.has(attachment.id)) return false;
        seen.add(attachment.id);
        return true;
    });
};

const hasValidClipAttachment = (event: MergeableMissionLogEvent): boolean =>
    event.clipJobs.some(
        (job) => job.status === "completed" && Boolean(job.attachmentId),
    );

/**
 * Collapse adjacent duplicate log events for display.
 *
 * Detection result payloads naturally differ from frame to frame, so adjacent
 * RTSP and thermal results group by topic. Only the first five visible result
 * events contribute hits and evidence attachments to their expanded row.
 */
export const mergeMissionLogEvents = <TEvent extends MergeableMissionLogEvent>(
    events: TEvent[],
): Array<MergedMissionLogEvent<TEvent>> => {
    const groups: TEvent[][] = [];

    for (const event of events) {
        const currentGroup = groups[groups.length - 1];
        if (currentGroup && areRepeats(currentGroup[currentGroup.length - 1], event)) {
            currentGroup.push(event);
        } else {
            groups.push([event]);
        }
    }

    return groups.map((group) => {
        const first = group[0];
        const isDetectionGroup = isDetectionResultTopic(first.type);
        const displayedEvents = isDetectionGroup ? group.slice(0, 5) : group;
        const eventWithClip = group.find(hasValidClipAttachment);

        return {
            ...first,
            attachments: uniqueAttachments(
                displayedEvents.flatMap((event) => event.attachments),
            ),
            clipJobs: eventWithClip
                ? eventWithClip.clipJobs
                : group.flatMap((event) => event.clipJobs),
            detectionHits: isDetectionGroup
                ? displayedEvents.flatMap((event) => extractDetectionHits(event.data))
                : undefined,
            mergedEventCount: group.length,
        };
    });
};
